<template>
  <div
    class="p-1 flex items-center justify-between gap-x-4 w-full bg-background-normal rounded-lg"
  >
    <div class="flex items-center gap-0.5">
      <!-- Current Platform Indicator -->
      <div class="relative">
        <div class="platform-indicator w-8 h-8 p-1.5 flex items-center justify-center rounded-md flex-shrink-0"
        @mouseenter="platformIndicatorHovered = true" @mouseleave="platformIndicatorHovered = false">
          <component :is="platformIcon" class="text-icon-normal" />
        </div>
        <transition>
          <div v-if="platformIndicatorHovered" class="platform-indicator-tooltip w-max">
            <BaseText type="label" size="xs" class="text-white px-1.5 py-1">
              Currently viewing {{ platformName }} metrics
            </BaseText>
          </div>
        </transition>
      </div>
      <!-- Preset info -->
      <SelectedPresetDropdown
        :selected-preset="getLocalPreset"
        :has-selected-preset="hasSelectedPreset"
        @handleShowModal="showCreateModal = true"
        @handleSelect="updateCurrentPreset"
      />
    </div>
    
    <!-- Preset actions -->
    <div class="flex items-center gap-1">
      <button
        :disabled="!hasSelectedPreset && getIsCustomPreset"
        class="preset-btn px-2.5 py-1.5 rounded-md transition-colors"
        @click="showEditModal = true"
      >
        <BaseText
          type="label"
          size="sm"
          class="text-text-muted truncate"
        >
          Edit
        </BaseText>
      </button>
      <!-- Divider -->
      <div class="border-l border-border-hover h-3" />
      <button
        class="preset-btn px-2.5 py-1.5 rounded-md transition-colors"
        @click="handleNewDerivedPreset"
      >
        <BaseText
          type="label"
          size="sm"
          class="text-text-muted whitespace-nowrap"
        >
          Save new Preset
        </BaseText>
      </button>
      <!-- Divider -->
      <div class="border-l border-border-hover h-3" />
      <button
        class="preset-btn px-2.5 py-1.5 rounded-md transition-colors"
        :disabled="!canUpdatePreset || (!hasSelectedPreset && getIsCustomPreset)"
        @click="handlePresetSave(getLocalPreset)"
      >
        <div
          v-if="loading.updatePreset"
          class="w-24 text-center"
        >
          <BaseLoadingSpinnerCircle class="mx-auto" />
        </div>
        <BaseText
          v-else
          type="label"
          size="sm"
          class="whitespace-nowrap"
        >
          Update Preset
        </BaseText>
      </button>
    </div>
    <!-- Modals -->
    <EditPresetModal
      v-if="showEditModal"
      key="edit-preset"
      @close="handleModalClose"
      @save="handlePresetSave"
      @delete="handlePresetDelete"
    />
    <EditPresetModal
      v-if="showCreateModal"
      key="new-preset"
      is-new
      :should-inherit="shouldInheritColumns"
      @close="handleModalClose"
      @save="handleNewPreset"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { platformNameMap, platformIconMap } from '@/utils/lens/dataSourceUtils'
import _ from 'lodash'
import { mixin as clickaway } from 'vue-clickaway2'
import LensAPI from '@/api/lens'
// Components
import SelectedPresetDropdown from '../SelectedPresetDropdown.vue'
import EditPresetModal from './EditPresetModal.vue'

export default {
  name: 'ManagePresetHeader',
  components: {
    EditPresetModal,
    SelectedPresetDropdown
  },
  mixins: [clickaway],
  props: {
    hasSelectedPreset: {
      type: Boolean,
      default: () => false
    }
  },
  data () {
    return {
      showEditModal: false,
      showCreateModal: false,
      isExpanded: false,

      shouldInheritColumns: false,

      loading: {
        updatePreset: false
      },

      platformIndicatorHovered: false
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getActiveLensId']),
    ...mapGetters('MetricsModule', ['getMetricsPlatform', 'getMetricLookup', 'getLocalPreset', 'getSelectedPreset', 'getPresets', 'getIsCustomPreset']),
    canUpdatePreset () {
      const originalPreset = this.getPresets.find(
        (preset) => preset.id === this.getLocalPreset.id
      )
      return (!this.hasSelectedPreset && this.getIsCustomPreset) ? false : !_.isEqual(
        originalPreset?.table_columns,
        this.getLocalPreset?.table_columns
      )
    },
    platformName () {
      return platformNameMap[this.getMetricsPlatform]
    },
    platformIcon () {
      return platformIconMap[`mono-${this.getMetricsPlatform}`]
    }
  },
  methods: {
    ...mapActions('MetricsModule', ['updatePreset', 'setPresetById', 'fetchPresets', 'createPreset']),
    async handlePresetSave (toUpdate) {
      this.loading.updatePreset = true
      try {
        await this.updatePreset(toUpdate)
        this.$showAlert({ type: 'success', message: 'Preset successfully updated' })
      } catch (e) {
        this.$showAlert({ type: 'error', message: 'Could not update preset' })
        console.error(e)
      } finally {
        this.loading.updatePreset = false
      }
    },
    handleModalClose () {
      this.showEditModal = false
      this.showCreateModal = false
      // Reset on close, init on open
      this.shouldInheritColumns = false
    },
    handleNewDerivedPreset () {
      this.shouldInheritColumns = true
      this.showCreateModal = true
    },
    updateCurrentPreset (presetId) {
      this.setPresetById({ id: presetId })
      this.$emit('selectPreset', presetId)
    },
    async handleNewPreset (toSave) {
      try {
        const newPresetId = await this.createPreset(toSave)
        console.log('NEW PRESET ID', newPresetId)
        if (newPresetId) {
          this.setPresetById({ id: newPresetId })
          this.$showAlert({
            type: 'success',
            message: 'Preset created successfully'
          })
        }
      } catch (error) {
        this.$showAlert({ type: 'error', message: error.message })
      }
    },
    async handlePresetDelete (presetId) {
      try {
        await LensAPI.LensPreset.remove(presetId)
        await this.fetchPresets()
        if (this.getLocalPreset.id === presetId) {
          const defaultPreset = this.getPresets.find(preset => preset.is_default)
          if (defaultPreset) {
            this.setPresetById({ id: defaultPreset.id })
          } else {
            this.setPresetById({ id: null })
          }
        }
        this.$showAlert({
          type: 'success',
          message: 'Preset deleted successfully'
        })
      } catch (e) {
        this.$showAlert({ type: 'error', message: e.message })
      }
    }
  }
}
</script>

<style scoped>
.preset-tag {
  box-shadow: 0px 0px 0.25px 0.75px rgba(6, 7, 15, 0.02),
    0px 1.5px 1.5px -0.75px rgba(6, 7, 15, 0.08),
    0px 3px 3px -1.5px rgba(6, 7, 15, 0.04);
}

.platform-indicator {
  background-color: transparent;
  box-shadow: none;
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.platform-indicator:hover {
  background-color: white;
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.08), 0 0 0 1px #EBEFF3;
}
.platform-indicator-tooltip {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  border-radius: 8px;
  background: rgba(6, 7, 16, 0.88);
  backdrop-filter: blur(6px);
  padding: 2px;
  z-index: 1000;
  pointer-events: none;
}

.preset-btn{
  color: #303546;
}
.preset-btn:not(:disabled):hover{
  background-color:#eceff3
}
.preset-btn:disabled{
  cursor: not-allowed;
  color:#a4abb8;
  opacity: 0.5;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>
